import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { nomenclature as nomenclatureApi } from '@/api/catalogs';
import { productDisassemblies, type ProductDisassemblyListItem } from '@/api/productDisassemblies';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames, agOrgCol } from '@/shared/hooks/useOrgNames';

const PERMISSION = 'production.assembly.manage';

export default function ProductDisassembliesPage() {
  const navigate = useNavigate();
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const f = useDocListFilters();
  const orgNames = useOrgNames();

  // Серверна пагінація (Норма #29) — за зразком ProductionReportsPage.
  const { datasource, total } = useInfiniteGridDatasource<ProductDisassemblyListItem>(
    (limit, offset, sort) => productDisassemblies.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { attachmentsType: 'doc.product_disassemblies' },
  );

  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomenclatureApi.listAll(),
  });
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  const cols = useMemo<ColDef<ProductDisassemblyListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: ProductDisassemblyListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: 'Номер', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'disassembled_nomenclature_id', headerName: 'Набір', width: 220, sortable: false,
      valueFormatter: (p) => p.value ? nomName.get(p.value as string) ?? '' : '',
    },
    {
      field: 'disassembled_quantity', headerName: 'К-сть', width: 110, sortable: false,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'total_cost', headerName: 'Собівартість', width: 150, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null
        ? Number(p.value).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [nomName, orgNames]);

  const canCreate = f.orgId ? allowedOrgIds.has(f.orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
      ? `Немає прав створювати Розукомплектацію у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Розукомплектація номенклатури<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/product-disassemblies/new')}
        >
          + Нова розукомплектація
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<ProductDisassemblyListItem>
        deleteRow={{ remove: (id) => productDisassemblies.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="product-disassemblies"
        onRowDoubleClicked={(r) => navigate(`/product-disassemblies/${r.id}`)}
        getRowStyle={(p) => (p.data?.deletion_mark || p.data?.state === 'CANCELLED')
          ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
